/**
 * User Notices API Types
 * Based on /api/user-notices endpoints
 */

export type NoticePriority = 'low' | 'normal' | 'high' | 'urgent';

// Notice Type GUIDs from API documentation
export const NoticeTypeIds = {
  FRIEND_REQUEST: 'CC289ED3-A2A1-4800-95C7-198E3E86046E',
  FRIEND_REQUEST_ACCEPTED: 'D1B5F8A2-3C4E-4F6A-8B9C-1234567890AB',
  GROUP_INVITATION: '11111111-1111-1111-1111-111111111111',
  GROUP_JOIN_REQUEST: 'A1B2C3D4-E5F6-7890-1234-567890ABCDEF', // GENERAL type used for group join requests
  ADVENTURE_INVITATION: 'B2C3D4E5-F6A7-8901-2345-67890ABCDEF1',
  GENERAL: 'A1B2C3D4-E5F6-7890-1234-567890ABCDEF',
  SYSTEM: 'C3D4E5F6-A7B8-9012-3456-7890ABCDEF12',
  SESSION_INVITE: 'A5B6C7D8-E9F0-1234-5678-90ABCDEF1234',
  MODERATOR_APPROVAL_REQUEST: 'F4A5B6C7-D8E9-0123-4567-890ABCDEF456',
} as const;

// Data type identifiers in parsed notice data
export const NoticeDataTypes = {
  GROUP_JOIN_REQUEST: 'GroupJoinRequest',
} as const;

// Backend notice status enum
export enum NoticeStatus {
  New = 0,
  Read = 1,
  Acknowledged = 2,
  Deleted = 3,
}

// Notice action types
export interface NoticeAction {
  label: string;
  actionType: string;
  actionData: string;
}

// Friend Request Notice Data
export interface FriendRequestData {
  connectionRequestId?: string;
  requesterId?: string;
  requesterName?: string;
  requesterAvatarUrl?: string;
  requesterDescription?: string;
  receiverUserId?: string;
}

// Group Invitation Notice Data
export interface GroupInvitationData {
  groupId: string;
  groupName: string;
  invitedBy: string;
}

// Adventure Invitation Notice Data
export interface AdventureInvitationData {
  adventureId: string;
  adventureName: string;
  invitedBy: string;
}

// Group Join Request Notice Data
export interface GroupJoinRequestData {
  type: 'GroupJoinRequest';
  relatedId: string; // Group ID
  requesterId?: string; // User who requested to join (from notice.userId)
  requesterName?: string; // Resolved user name
  requesterAvatarUrl?: string; // Resolved user avatar
}

// Session Invite Notice Data
export interface SessionInviteNoticeData {
  SessionId: string;
  SessionName: string;
  AdventureId: string;
  AdventureName: string;
  InvitedBy: string;
  InviterName: string;
}

// Moderator Approval Request Notice Data
export interface ModeratorApprovalRequestData {
  sessionId: string
  adventureId: string
  nodeId: string
  nodeLabel?: string
  requestingUsers: string[]
}

export interface UserNotice {
  id: string;
  created: string; // Backend uses 'created' instead of 'timestamp'
  status: NoticeStatus; // Backend uses status enum instead of boolean flags
  type: string; // Backend uses GUID string for type
  quickMessage: string; // Backend uses 'quickMessage' instead of 'message'
  title: string;
  userId: string;
  expirationDate: string | null;
  iconUrl: string | null;
  actionsJson: string | null;
  data: string | null; // JSON string containing additional data

  // Computed properties for compatibility with UI
  timestamp?: string;
  message?: string;
  read?: boolean;
  acknowledged?: boolean;
  actions?: NoticeAction[];
  parsedData?: FriendRequestData | GroupInvitationData | AdventureInvitationData | SessionInviteNoticeData | ModeratorApprovalRequestData | any;
  priority?: NoticePriority;
}

export interface CreateUserNoticeRequest {
  type: string; // GUID of notice type
  title: string;
  quickMessage: string;
  data?: string; // JSON string
  iconUrl?: string;
  expirationDate?: string;
}

export interface UnreadCountResponse {
  count: number;
}
